/**
 * AttendanceQuickLog — 2026-02-26
 *
 * Popover that logs a single Five Below attendance/punctuality event
 * against a crew member. Used by:
 *   - Zone Sheet crew row chips ("log a call-off from the row you're
 *     already looking at")
 *   - Report Card attendance section (same shape, same endpoint)
 *
 * Emits `onLogged({event, snapshot})` on success so the caller can
 * update its own state without a full refetch.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogTitle,
} from "./ui/dialog";
import { WarningCircle, Prohibit, Clock, DoorOpen, CheckCircle, ShieldCheck, ArrowsClockwise, X as XIcon } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { key: "absence",    label: "Call-off",     tint: "bg-rose-50 text-rose-700 border-rose-200",     Icon: Prohibit,      counted: true },
  { key: "tardy",      label: "Tardy",        tint: "bg-amber-50 text-amber-700 border-amber-200",  Icon: Clock,         counted: true },
  { key: "left_early", label: "Left Early",   tint: "bg-orange-50 text-orange-700 border-orange-200", Icon: DoorOpen,   counted: true },
  { key: "ncns",       label: "No-Call/No-Show", tint: "bg-slate-900 text-white border-slate-900", Icon: WarningCircle, counted: true },
  { key: "fmla",         label: "FMLA / Protected",    tint: "bg-sky-50 text-sky-700 border-sky-200",     Icon: ShieldCheck,  counted: false },
  { key: "bereavement",  label: "Bereavement",         tint: "bg-sky-50 text-sky-700 border-sky-200",     Icon: ShieldCheck,  counted: false },
];

// Types where the crew "won't be here" — trigger the shift-action
// follow-up (remove or reassign the shift).
const OFF_SHIFT_TYPES = new Set(["absence", "ncns", "fmla", "bereavement"]);

/**
 * Props:
 *   - shiftAssignment: the crew's zone-sheet row for today (or null).
 *     When present AND the logged type is in OFF_SHIFT_TYPES, we show
 *     a follow-up "What about the shift?" step after logging.
 *   - reassignCandidates: array of {id, name} for the dropdown.
 *   - onShiftAction: async ({action:"remove"|"reassign", newCrewId?})
 *     — the caller (ZoneSheetPage) patches the actual zone-sheet.
 */
export default function AttendanceQuickLog({
  open, onOpenChange, crewId, crewName, actorId, defaultDate, onLogged,
  shiftAssignment = null,
  reassignCandidates = [],
  onShiftAction = null,
  initialStep = "log",
}) {
  // MST-anchored default date — UTC-slice would flip to tomorrow at
  // 5 PM MST, so a manager logging a late-shift attendance event would
  // see the WRONG default day. Fix class 2026-02-27.
  const _todayLocalMst = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
  const [type, setType] = useState("absence");
  const [date, setDate] = useState(defaultDate || _todayLocalMst());
  const [notes, setNotes] = useState("");
  const [minutesLate, setMinutesLate] = useState("");
  const [excused, setExcused] = useState(false);
  const [excusedReason, setExcusedReason] = useState("");
  const [saving, setSaving] = useState(false);
  // Step machine — after successful log, if the type is "won't be
  // here" AND we have a shift for them, flip to "shift-action" prompt
  // instead of closing.
  const [step, setStep] = useState("log");     // "log" | "shift-action"
  const [reassignTo, setReassignTo] = useState("");

  // When the dialog opens, honor the caller's requested starting step.
  // A crew member who's ALREADY logged out for the day opens straight on
  // "shift-action" (skip re-logging) so the manager just picks who covers.
  useEffect(() => {
    if (open) setStep(initialStep === "shift-action" && shiftAssignment ? "shift-action" : "log");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialStep]);

  const selectedType = TYPES.find((t) => t.key === type);
  const isBuiltInExcused = selectedType && !selectedType.counted;

  const reset = () => {
    setType("absence"); setDate(defaultDate || _todayLocalMst());
    setNotes(""); setMinutesLate(""); setExcused(false); setExcusedReason("");
    setStep("log"); setReassignTo("");
  };

  const closeAll = (fresh = true) => {
    onOpenChange(false);
    if (fresh) reset();
  };

  const submit = async () => {
    if (!crewId || !actorId) return;
    // Whether logging this type should chain into the "handle the shift"
    // step. Computed up-front so both the success path AND the
    // already-logged (409) catch path can route into shift-action.
    const shouldChainShift =
      OFF_SHIFT_TYPES.has(type) &&
      shiftAssignment &&
      (shiftAssignment.shift || shiftAssignment.coverage) &&
      onShiftAction;
    setSaving(true);
    try {
      const body = {
        actor_id: actorId,
        crew_id: crewId,
        date,
        type,
        notes: notes.trim(),
      };
      if (type === "tardy" && minutesLate) body.minutes_late = Number(minutesLate) || 0;
      if (!isBuiltInExcused && excused) {
        body.excused = true;
        body.excused_reason = excusedReason.trim() || "manager-excused";
      }
      const { data } = await axios.post(`${API}/attendance/events`, body);
      if (onLogged) onLogged(data);
      if (shouldChainShift) {
        toast.success(`Logged — now handle ${crewName || "their"} shift`);
        setStep("shift-action");
      } else {
        toast.success(
          (data.snapshot?.level && data.snapshot.level !== "clear")
            ? `Logged — ${crewName || "crew member"} is at ${data.snapshot.label}`
            : "Attendance event logged"
        );
        closeAll();
      }
    } catch (e) {
      // A 409 means an occurrence is ALREADY logged for this crew today
      // (one-per-day rule). That's not a dead-end — the manager still
      // needs to hand off the now-vacant shift. Don't trap them behind
      // the error: acknowledge the existing log and jump straight to the
      // shift-action step so they can reassign or remove the shift.
      // (User report 2026-09-16: "we need a way to swap even if it's
      // logged — the system should just know it's logged and ask who's
      // taking the shift.")
      const isAlreadyLogged = e?.response?.status === 409;
      if (isAlreadyLogged && shouldChainShift) {
        toast.info(`${crewName || "They're"} already marked out — pick who's covering the shift`);
        setStep("shift-action");
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't log attendance");
      }
    } finally {
      setSaving(false);
    }
  };

  const doShiftAction = async (action) => {
    if (!onShiftAction) return;
    setSaving(true);
    try {
      if (action === "remove") {
        await onShiftAction({ action: "remove" });
        toast.success("Shift removed from today's sheet");
      } else if (action === "reassign") {
        if (!reassignTo) {
          toast.error("Pick who's covering the shift.");
          setSaving(false);
          return;
        }
        await onShiftAction({ action: "reassign", newCrewId: reassignTo });
        toast.success("Shift reassigned");
      } else if (action === "keep") {
        toast.info("Shift kept as-is on the sheet.");
      }
      closeAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the shift");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeAll(); else onOpenChange(true); }}>
      <DialogContent
        className="p-0 gap-0 max-w-md w-[calc(100%-16px)] rounded-3xl bg-white"
        data-testid="attendance-quick-log"
      >
        <DialogTitle className="sr-only">Log attendance event for {crewName || "crew"}</DialogTitle>
        <div className="px-5 pt-5 pb-3 border-b border-slate-200 bg-slate-50">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            {step === "log" ? "Log attendance" : "What about their shift?"}
          </p>
          <p className="text-lg font-black text-slate-900 mt-0.5" data-testid="attendance-quick-log-crew-name">
            {crewName || "Crew"}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {step === "log"
              ? "Rolls up in the 6-month window used for progressive discipline."
              : `They were scheduled ${shiftAssignment?.shift || "today"}${shiftAssignment?.coverage ? ` · ${shiftAssignment.coverage}` : ""}.`}
          </p>
        </div>

        {step === "log" && (
          <>
        <div className="p-5 space-y-4">
          {/* Type picker */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Type</p>
            <div className="grid grid-cols-2 gap-1.5">
              {TYPES.map((t) => {
                const active = type === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setType(t.key)}
                    className={`text-left rounded-lg border px-2.5 py-2 flex items-center gap-2 text-xs font-black transition-all ${active ? t.tint + " ring-2 ring-offset-1 ring-slate-900/10" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                    data-testid={`attendance-type-${t.key}`}
                  >
                    <t.Icon weight="fill" className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{t.label}</span>
                    {!t.counted && (
                      <span className="ml-auto text-[8px] font-bold uppercase tracking-wide text-sky-700 flex-shrink-0">no pt</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={_todayLocalMst()}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
              data-testid="attendance-date"
            />
          </div>

          {/* Minutes late (tardy only) */}
          {type === "tardy" && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Minutes late</label>
              <input
                type="number"
                min={1}
                max={480}
                value={minutesLate}
                onChange={(e) => setMinutesLate(e.target.value)}
                placeholder="e.g. 12"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                data-testid="attendance-minutes-late"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Notes (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason / context…"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 resize-none"
              data-testid="attendance-notes"
            />
          </div>

          {/* Excuse checkbox (only for countable types — FMLA/bereavement
              are inherently excused so we hide this) */}
          {!isBuiltInExcused && (
            <div className="rounded-lg bg-sky-50 border border-sky-200 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={excused}
                  onChange={(e) => setExcused(e.target.checked)}
                  className="mt-0.5"
                  data-testid="attendance-excused"
                />
                <div className="flex-1">
                  <p className="text-xs font-black text-sky-900">Mark as EXCUSED — do not count toward occurrences</p>
                  <p className="text-[10px] text-sky-700 mt-0.5">Requires a documented reason. This is audit-logged.</p>
                  {excused && (
                    <input
                      type="text"
                      value={excusedReason}
                      onChange={(e) => setExcusedReason(e.target.value)}
                      placeholder="Reason (e.g. jury duty, doctor's note)"
                      className="mt-2 w-full rounded-md border border-sky-300 px-2 py-1 text-xs focus:border-sky-400 focus:ring-1 focus:ring-sky-200"
                      data-testid="attendance-excused-reason"
                    />
                  )}
                </div>
              </label>
            </div>
          )}
        </div>

        <div className="px-5 pb-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => closeAll()}
            className="text-xs font-black text-slate-500 hover:text-slate-800 px-3 py-2"
            data-testid="attendance-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="text-xs font-black text-white bg-slate-900 hover:bg-slate-800 rounded-lg px-4 py-2 disabled:opacity-40 flex items-center gap-1.5"
            data-testid="attendance-submit"
          >
            <CheckCircle weight="fill" className="w-3.5 h-3.5" /> Log event
          </button>
        </div>
          </>
        )}

        {step === "shift-action" && (
          <div className="p-5 space-y-3" data-testid="attendance-shift-action">
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
              <p className="text-xs font-black text-rose-900">
                {crewName || "This crew member"} won&apos;t be on the floor
              </p>
              {shiftAssignment?.shift && (
                <p className="text-[11px] text-rose-800 mt-1">
                  Scheduled: <span className="font-bold">{shiftAssignment.shift}</span>
                  {shiftAssignment.break_time ? ` · break ${shiftAssignment.break_time}` : ""}
                </p>
              )}
              {shiftAssignment?.coverage && (
                <p className="text-[11px] text-rose-800">
                  Coverage: <span className="font-bold">{shiftAssignment.coverage}</span>
                </p>
              )}
            </div>

            {/* Option 1: Remove */}
            <button
              type="button"
              onClick={() => doShiftAction("remove")}
              disabled={saving}
              className="w-full text-left rounded-lg border border-rose-300 bg-white hover:bg-rose-50 px-3 py-3 flex items-center gap-3 disabled:opacity-40"
              data-testid="attendance-shift-remove"
            >
              <XIcon weight="bold" className="w-5 h-5 text-rose-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-black text-slate-900">Remove shift &amp; tasks</p>
                <p className="text-[11px] text-slate-500">Delete their row from today&apos;s zone sheet entirely.</p>
              </div>
            </button>

            {/* Option 2: Reassign */}
            <div className="rounded-lg border border-amber-300 bg-white px-3 py-3">
              <div className="flex items-start gap-3">
                <ArrowsClockwise weight="bold" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-black text-slate-900">Reassign to another crew member</p>
                  <p className="text-[11px] text-slate-500">Their shift + break + coverage carry over to the new person.</p>
                  <div className="mt-2 flex gap-2">
                    <select
                      value={reassignTo}
                      onChange={(e) => setReassignTo(e.target.value)}
                      className="flex-1 text-xs rounded border border-slate-200 px-2 py-1.5 bg-white"
                      data-testid="attendance-shift-reassign-picker"
                    >
                      <option value="">Pick who&apos;s covering…</option>
                      {reassignCandidates.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => doShiftAction("reassign")}
                      disabled={saving || !reassignTo}
                      className="text-xs font-black text-white bg-amber-600 hover:bg-amber-700 rounded px-3 disabled:opacity-40"
                      data-testid="attendance-shift-reassign-confirm"
                    >
                      Reassign
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Option 3: Keep as-is (rare — for the manager who prefers
                to sort it later) */}
            <button
              type="button"
              onClick={() => doShiftAction("keep")}
              disabled={saving}
              className="w-full text-center text-[11px] font-bold text-slate-500 hover:text-slate-800 py-2"
              data-testid="attendance-shift-keep"
            >
              Keep shift on the sheet — I&apos;ll sort it later
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
